import { useCallback } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { Configuration } from '../domain/types';
import { useAsyncResource } from './use-async-resource';

export interface UseConfigurationParams {
  /**
   * Skip fetching entirely — used while the route has not supplied an id
   * yet, so the module never asks the BFF for an empty id.
   */
  skip?: boolean;
}

export interface UseConfigurationReturn {
  configuration: Configuration | null;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}

/** Fetches a single configuration by id through the `PartnerDataClient` port. */
export const useConfiguration = (
  id: string,
  { skip = false }: UseConfigurationParams = {},
): UseConfigurationReturn => {
  const client = usePartnerDataClient();

  const fetchConfiguration = useCallback(
    (): Promise<Configuration | null> =>
      skip || !id ? Promise.resolve(null) : client.getConfiguration(id),
    [client, id, skip],
  );

  const { data, isLoading, hasError, refetch } =
    useAsyncResource<Configuration | null>(fetchConfiguration, null);

  return { configuration: data, isLoading, hasError, refetch };
};
